import React from 'react';
import { Crown, Check, AlertCircle } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { redirectToCustomerPortal } from '../../services/stripe/checkoutService';
import { StripePaymentButton } from './StripePaymentButton';

export function SubscriptionSettings() {
  const { subscription, loading, error } = useSubscription();
  const navigate = useNavigate();
  const [portalError, setPortalError] = React.useState<string | null>(null);

  const handleManageSubscription = async () => {
    try {
      await redirectToCustomerPortal();
    } catch (err) {
      console.error('Error redirecting to customer portal:', err);
      setPortalError('Failed to open subscription management. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="card space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[var(--accent-color)]/20 flex items-center justify-center">
            <Crown className="w-4 h-4 text-[var(--accent-color)]" />
          </div>
          <h2 className="text-lg font-semibold">Subscription</h2>
        </div>
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--accent-color)] border-t-transparent mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-[var(--accent-color)]/20 flex items-center justify-center">
          <Crown className="w-4 h-4 text-[var(--accent-color)]" />
        </div>
        <h2 className="text-lg font-semibold">Subscription</h2>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 text-red-500 rounded-xl">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">Failed to load subscription status. Please try again later.</p>
        </div>
      )}

      {portalError && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 text-red-500 rounded-xl">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{portalError}</p>
        </div>
      )}

      {subscription?.status === 'active' ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-[var(--accent-color)]/10 rounded-xl">
            <div>
              <div className="font-medium">Premium Plan</div>
              <div className="text-sm text-gray-400">
                Next billing date: {new Date(subscription.current_period_end).toLocaleDateString()}
              </div>
            </div>
            <div className="flex items-center gap-2 text-[var(--accent-color)]">
              <Check className="w-5 h-5" />
              <span>Active</span>
            </div>
          </div>

          <button
            onClick={handleManageSubscription}
            className="w-full px-4 py-2 bg-black/20 rounded-xl hover:bg-black/30 transition-colors"
          >
            Manage Subscription
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-4 bg-black/20 rounded-xl">
            <div className="font-medium mb-1">Free Plan</div>
            <p className="text-sm text-gray-400">
              Upgrade to Premium to unlock all features
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <StripePaymentButton
              amount={9.99}
              onSuccess={() => window.location.reload()}
              onError={(error) => setPortalError(error)}
            />
            <button
              onClick={() => navigate('/subscription')}
              className="text-sm text-[var(--accent-color)] hover:underline"
            >
              View all plans
            </button>
          </div>
        </div>
      )}
    </div>
  );
}